/**
 * WHAT: Tiered status ribbon that distinguishes clean success from partial-success
 *       (where some rows were skipped) and informational outcomes, so users can
 *       immediately tell whether an action fully did what they asked.
 * WHY:  A single green Bootstrap alert conflated "all N saved" with "applied 3,
 *       skipped 2 (self-attestation)". Managers missed the skipped-row detail
 *       because it was buried in a green ribbon that reads as unqualified success.
 */
import React, { useEffect } from 'react';

const TONE_STYLES = {
  success: {
    background: '#d1e7dd',
    border: '#badbcc',
    accent: '#198754',
    text: '#0f5132',
    icon: 'fa-check-circle'
  },
  warning: {
    background: '#fff3cd',
    border: '#ffecb5',
    accent: '#d69e2e',
    text: '#664d03',
    icon: 'fa-exclamation-triangle'
  },
  info: {
    background: '#cff4fc',
    border: '#b6effb',
    accent: '#0dcaf0',
    text: '#055160',
    icon: 'fa-info-circle'
  },
  danger: {
    background: '#f8d7da',
    border: '#f5c2c7',
    accent: '#dc3545',
    text: '#842029',
    icon: 'fa-times-circle'
  }
};

const StatusRibbon = ({ tone = 'success', title, details, onDismiss, autoDismissMs }) => {
  const style = TONE_STYLES[tone] || TONE_STYLES.success;

  useEffect(() => {
    if (!autoDismissMs || tone !== 'success' || !onDismiss) return;
    const timer = setTimeout(onDismiss, autoDismissMs);
    return () => clearTimeout(timer);
  }, [autoDismissMs, tone, onDismiss]);

  if (!title && !details) return null;

  const detailLines = Array.isArray(details)
    ? details.filter(Boolean)
    : details ? [details] : [];

  return (
    <div
      role="alert"
      style={{
        position: 'relative',
        padding: '14px 44px 14px 18px',
        background: style.background,
        border: `1px solid ${style.border}`,
        borderLeft: `5px solid ${style.accent}`,
        borderRadius: '6px',
        color: style.text,
        marginBottom: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <i
          className={`fas ${style.icon}`}
          style={{ color: style.accent, fontSize: '1.25rem', marginTop: '2px', flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          {title && (
            <div style={{ fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.4 }}>
              {title}
            </div>
          )}
          {detailLines.length > 0 && (
            <ul style={{
              margin: title ? '6px 0 0 0' : 0,
              paddingLeft: '18px',
              fontSize: '0.95rem',
              lineHeight: 1.5
            }}>
              {detailLines.map((line, idx) => (
                <li key={idx}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {onDismiss && (
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onDismiss}
          style={{
            position: 'absolute',
            top: '10px',
            right: '12px',
            background: 'transparent',
            border: 'none',
            fontSize: '20px',
            lineHeight: 1,
            cursor: 'pointer',
            color: style.text,
            opacity: 0.6,
            padding: 0
          }}
          onMouseOver={(e) => (e.currentTarget.style.opacity = '1')}
          onMouseOut={(e) => (e.currentTarget.style.opacity = '0.6')}
        >×</button>
      )}
    </div>
  );
};

export default StatusRibbon;
